import { useState } from "react";
import { DOCTOR_NONE_TEXT, doctorNameAsTyped, doctorNameKey, doctorNamesClose, normalizeDoctorName, type Doctor } from "@probetalab/contracts";
import { get, run } from "../lib/api.js";
import { useDebounced, useQuery, errorMessage } from "../lib/useQuery.js";
import { Button, Field, Input, Pill, SearchInput, Sheet } from "./ui.js";
import { useToast } from "../lib/toast.js";

export interface DoctorChoice {
  doctor_id: string | null;
  doctor_text: string | null;
  display: string;
}

export const DOCTOR_NONE: DoctorChoice = { doctor_id: null, doctor_text: null, display: DOCTOR_NONE_TEXT };

/** Requesting doctor: the directory, "A quien corresponda", or a new doctor registered on the spot. */
export function DoctorPicker({ value, onChange, compact = false }: { value: DoctorChoice; onChange: (v: DoctorChoice) => void; compact?: boolean }) {
  const [q, setQ] = useState("");
  const dq = useDebounced(q.trim());
  const [adding, setAdding] = useState(false);
  const query = useQuery(() => get("doctors", { query: { q: dq, limit: 8 } }), [dq], { enabled: dq.length >= 2 });

  return (
    <div className="stack">
      {/* `compact` (Recepción's one-screen layout): the pills, the directory search and a «Nuevo médico» icon button share one row. */}
      <div className={compact ? "doctor-compact-row" : "stack"}>
      <div className="pills">
        <Pill on={value.doctor_id === null && value.doctor_text === null} onClick={() => onChange(DOCTOR_NONE)}>
          {DOCTOR_NONE_TEXT}
        </Pill>
        {/* Free text (from a quote or the previous system): shown so an edit does not hide it. */}
        {!value.doctor_id && value.doctor_text && (
          <Pill on onClick={() => onChange(DOCTOR_NONE)} icon="x">
            {value.doctor_text}
          </Pill>
        )}
        {value.doctor_id && (
          <Pill on onClick={() => onChange(DOCTOR_NONE)} icon="x">
            {value.display}
          </Pill>
        )}
        {!compact && (
          <Button variant="ghost" size="sm" icon="plus" onClick={() => setAdding(true)}>
            Nuevo médico
          </Button>
        )}
      </div>
      <SearchInput icon="stethoscope" value={q} onChange={(e) => setQ(e.target.value)} placeholder={compact ? "Buscar…" : "Buscar médico en el directorio…"} aria-label="Buscar médico" />
      {compact && (
        <Button variant="ghost" size="sm" icon="plus" aria-label="Nuevo médico" onClick={() => setAdding(true)}>
          <span className="doctor-new-label">Nuevo</span>
        </Button>
      )}
      </div>
      {dq.length >= 2 && (
        <div className="card" style={{ overflow: "hidden" }}>
          {query.data?.items.length ? (
            query.data.items.map((d) => (
              <button
                key={d.id}
                type="button"
                className="list-row clickable"
                onClick={() => {
                  onChange({ doctor_id: d.id, doctor_text: null, display: d.full_name });
                  setQ("");
                }}
              >
                <span className="grow">
                  <div className="primary">{d.full_name}</div>
                  <div className="secondary">{[d.specialty, d.cedula ? `Céd. ${d.cedula}` : null].filter(Boolean).join(" · ") || "Sin especialidad"}</div>
                </span>
              </button>
            ))
          ) : (
            <div className="list-row faint">{query.loading ? "Buscando…" : "No está en el directorio. Agrégalo con «Nuevo médico»."}</div>
          )}
        </div>
      )}
      <NewDoctorSheet
        open={adding}
        onClose={() => setAdding(false)}
        onSaved={(d) => {
          onChange({ doctor_id: d.id, doctor_text: null, display: d.full_name });
          setAdding(false);
        }}
      />
    </div>
  );
}

export function NewDoctorSheet({ open, onClose, onSaved }: { open: boolean; onClose: () => void; onSaved: (d: Doctor) => void }) {
  const toast = useToast();
  const [name, setName] = useState("");
  const [specialty, setSpecialty] = useState("");
  const [cedula, setCedula] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const similar = useSimilarDoctors(name, open);

  /** Clears the form and hands the doctor (new, or the existing one the user picked instead) back. */
  function reset(d: Doctor) {
    setName("");
    setSpecialty("");
    setCedula("");
    setPhone("");
    onSaved(d);
  }

  async function save() {
    setBusy(true);
    try {
      const d = await run<"doctor.upsert", Doctor>("doctor.upsert", {
        doctor_id: null,
        full_name: normalizeDoctorName(name),
        specialty: specialty.trim() || null,
        cedula: cedula.trim() || null,
        phone: phone.replace(/\D/g, "") || null,
        email: null,
        is_active: true,
      });
      toast.success(`Médico registrado: ${d.full_name}`);
      reset(d);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="Nuevo médico"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="primary" icon="check" onClick={() => void save()} busy={busy} disabled={name.trim().length < 3}>
            Guardar médico
          </Button>
        </>
      }
    >
      <Field label="Nombre completo">
        <Input value={name} onChange={(e) => setName(doctorNameAsTyped(e.target.value))} placeholder="Dr. / Dra. Nombre Apellido" autoFocus />
      </Field>
      {similar.length > 0 && (
        <div className="stack" role="group" aria-label="¿Es alguno de estos?">
          <div className="secondary">¿Es alguno de estos?</div>
          <div className="card" style={{ overflow: "hidden" }}>
            {similar.map((d) => (
              <button key={d.id} type="button" className="list-row clickable" onClick={() => reset(d)}>
                <span className="grow">
                  <div className="primary">{d.full_name}</div>
                  <div className="secondary">{[d.specialty, d.cedula ? `Céd. ${d.cedula}` : null].filter(Boolean).join(" · ") || "Sin especialidad"}</div>
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
      <div className="grid-3">
        <Field label="Especialidad" optional>
          <Input value={specialty} onChange={(e) => setSpecialty(e.target.value)} />
        </Field>
        <Field label="Cédula" optional>
          <Input value={cedula} onChange={(e) => setCedula(e.target.value)} inputMode="numeric" />
        </Field>
        <Field label="Celular" optional>
          <Input value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="numeric" />
        </Field>
      </div>
    </Sheet>
  );
}

/**
 * Existing doctors that probably are the one being typed (same name key, one letter apart, or the
 * typed words match words of theirs): the lab re-created doctors because the directory did not find
 * them (2026-11-03). Each typed word is searched on its own so a typo in one of them still finds the
 * doctor through the others; `doctorNamesClose` then decides.
 */
function useSimilarDoctors(name: string, enabled: boolean): Doctor[] {
  const typed = useDebounced(name);
  const words = doctorNameKey(typed).split(" ").filter((w) => w.length >= 3).slice(0, 4);
  const on = enabled && typed.trim().length >= 5 && words.length > 0;
  const query = useQuery(
    async () => {
      const pages = await Promise.all(words.map((w) => get("doctors", { query: { q: w, limit: 50 } })));
      const byId = new Map<string, Doctor>();
      for (const p of pages) for (const d of p.items) byId.set(d.id, d);
      return [...byId.values()];
    },
    [words.join(" ")],
    { enabled: on },
  );
  if (!on) return [];
  return (query.data ?? []).filter((d) => doctorNamesClose(typed, d.full_name)).slice(0, 5);
}
